import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  IconButton,
  Chip,
  Collapse,
  Divider,
  Paper,
  Stack,
  CircularProgress,
  Tooltip,
  Alert,
  Fade,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AdminQuizAPIs from "../../../utilities/api/quiz/AdminQuizAPIs";
import AdminModuleAPIs from "../../../utilities/api/tutorials/AdminModuleAPIs";




// ─── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ label, value, unit, color = "text.primary" }) => (
  <Paper
    variant="outlined"
    sx={{ flex: 1, minWidth: { xs: 90, sm: 110 }, p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing={0.8}
      display="block"
      mb={0.5}
    >
      {label}
    </Typography>
    <Box display="flex" alignItems="baseline" gap={0.5}>
      <Typography variant="h4" fontWeight={700} color={color} lineHeight={1}>
        {value}
      </Typography>
      {unit && (
        <Typography variant="caption" color="text.secondary">
          {unit}
        </Typography>
      )}
    </Box>
  </Paper>
);

// ─── Question Card ────────────────────────────────────────────────────────────
const QuestionCard = ({ q, i, isEditing, updateQuestion, updateOption, removeQuestion }) => {
  const [open, setOpen] = useState(true);

  return (
    <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden" }}>
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        px={{ xs: 1.5, sm: 2 }}
        py={1.5}
        bgcolor="grey.50"
        onClick={() => !isEditing && setOpen((o) => !o)}
        sx={{ cursor: !isEditing ? "pointer" : "default", userSelect: "none" }}
      >
        <Box
          sx={{
            width: 28, height: 28, borderRadius: 1.5,
            bgcolor: "primary.main", color: "primary.contrastText",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
          }}
        >
          {i + 1}
        </Box>

        <Typography variant="body2" fontWeight={600} flex={1} noWrap color="text.primary">
          {q.question || (
            <Box component="span" sx={{ color: "text.disabled", fontStyle: "italic" }}>
              Untitled question…
            </Box>
          )}
        </Typography>

     

        {isEditing ? (
          <Tooltip title="Remove question">
            <IconButton
              size="small" color="error"
              onClick={(e) => { e.stopPropagation(); removeQuestion(i); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: "text.disabled",
              transition: "transform 0.2s",
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          />
        )}
      </Box>

      <Collapse in={open || isEditing}>
        <Divider />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
          {isEditing ? (
            <Stack spacing={2}>
              <TextField
                label="Question Text"
                fullWidth multiline minRows={2} size="small"
                value={q.question}
                placeholder="Enter your question…"
                onChange={(e) => updateQuestion(i, "question", e.target.value)}
              />

              {/* Type + Correct Answer: 2-col on sm+, stacked on xs */}



       <TextField
  select
  label="Correct Answer"
  size="small"
  value={q.correct_answer || ""}
  onChange={(e) => updateQuestion(i, "correct_answer", e.target.value)}
>
  {["A", "B", "C", "D"].map((opt) => (
    <MenuItem key={opt} value={opt}>
      Option {opt}
    </MenuItem>
  ))}
</TextField>
              

              {/* MCQ options: 2-col on sm+, 1-col on xs */}
              
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
                  {["A", "B", "C", "D"].map((key) => (
                    <TextField
                      key={key}
                      label={`Option ${key}`} size="small"
                      value={q.options[key]}
                      onChange={(e) => updateOption(i, key, e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <Typography variant="caption" color="primary" fontWeight={700} mr={0.5}>
                            {key}
                          </Typography>
                        ),
                      }}
                    />
                  ))}
                </Box>
              
            </Stack>
          ) : (
            <Box>
              
                
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1} mb={1}>
                  {["A", "B", "C", "D"].map((key) => {
                    const isCorrect = q.correct_answer === key;
                    return (
                      <Box
                        key={key}
                        display="flex" alignItems="center" gap={1}
                        p={1} px={1.5} borderRadius={1.5}
                        border="1.5px solid"
                        borderColor={isCorrect ? "primary.main" : "divider"}
                        sx={{ bgcolor: isCorrect ? "rgba(25, 118, 210, 0.06)" : "grey.50" }}
                      >
                        <Typography variant="caption" fontWeight={700}
                          color={isCorrect ? "primary.main" : "text.disabled"} minWidth={14}>
                          {key}
                        </Typography>
                        <Typography variant="body2" fontWeight={isCorrect ? 600 : 400} flex={1}>
                          {q.options[key]}
                        </Typography>
                        {isCorrect && <CheckCircleIcon sx={{ fontSize: 14, color: "primary.main" }} />}
                      </Box>
                    );
                  })}
                </Box>
              


            
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const QuizDetailPage = ({ quiz, onBack, startEdit = false }) => {
  const [isEditing, setIsEditing] = useState(startEdit);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [allModules, setAllModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState(quiz.modules || []);

  useEffect(() => {
    const loadModules = async () => {
      try {
        const res = await AdminModuleAPIs.ModuleListGet();
        const all = res || [];
        setAllModules(all);
        if (quiz.modules && quiz.modules.length > 0) {
          setSelectedModules(all.filter((m) => quiz.modules.includes(m.id)));
        }
      } catch (err) {
        console.error("Failed to load modules", err);
      }
    };
    loadModules();
  }, [quiz]);

  const [formData, setFormData] = useState({
    title: quiz.title,
    estimated_time_req: quiz.estimated_time_req,
    passing_score: quiz.passing_score,
    attempts_allowed: quiz.attempts_allowed,
    due_date: quiz.due_date,
  });

  const [questions, setQuestions] = useState(quiz.questions || []);

  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  const addQuestion = () =>
    setQuestions((prev) => [
      ...prev,
      { question: "", type: "MCQ",  options: { A: "", B: "", C: "", D: "" }, correct_answer: "" },
    ]);

  const updateQuestion = (i, field, value) => {
    const copy = [...questions];
    copy[i][field] = value;
    setQuestions(copy);
  };

  const updateOption = (i, key, value) => {
    const copy = [...questions];
    copy[i].options[key] = value;
    setQuestions(copy);
  };

  const removeQuestion = (i) =>
    setQuestions((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      await AdminQuizAPIs.UpdateQuiz(quiz.id, {
        ...formData,
        modules: selectedModules.map((m) => m.id),
        questions,
      });
      setIsEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to update quiz");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: quiz.title,
      estimated_time_req: quiz.estimated_time_req,
      passing_score: quiz.passing_score,
      attempts_allowed: quiz.attempts_allowed,
      due_date: quiz.due_date,
    });
    setSelectedModules(quiz.modules || []);
    setQuestions(quiz.questions || []);
    setIsEditing(false);
  };

  return (
    <Box minHeight="100vh" bgcolor="grey.50">

      {/* ── Sticky Top Bar ── */}
      <Paper
        elevation={0} square
        sx={{
          position: "sticky", top: 0, zIndex: 10,
          borderBottom: "1px solid", borderColor: "divider",
          // Responsive padding + auto height to wrap gracefully on mobile
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1, sm: 0 },
          minHeight: 56,
          display: "flex", alignItems: "center",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          color="inherit"
          sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0 }}
        >
          Back
        </Button>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

        {/* Title — inline on sm+, hidden on xs (shown as second line below) */}
        <Typography
          variant="subtitle1" fontWeight={600} flex={1} noWrap
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          {formData.title}
        </Typography>

        {/* Right-side controls */}
        <Box display="flex" alignItems="center" gap={1} ml={{ xs: "auto", sm: 0 }} flexShrink={0}>
          <Chip
            label={isEditing ? "Editing" : "View"}
            color={isEditing ? "warning" : "default"}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />

          {isEditing ? (
            <Stack direction="row" spacing={1}>
              {/* Cancel hidden on mobile — surfaced at bottom of page instead */}
              <Button
                variant="outlined" size="small"
                onClick={handleCancel} disabled={saving}
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained" size="small"
                onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained" size="small"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
            >
              {/* Shorter label on mobile */}
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Edit Quiz</Box>
              <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Edit</Box>
            </Button>
          )}
        </Box>

        {/* Mobile-only second-line title */}
        <Typography
          variant="subtitle2" fontWeight={600} noWrap
          sx={{ display: { xs: "block", sm: "none" }, width: "100%", pb: 0.25 }}
        >
          {formData.title}
        </Typography>
      </Paper>

      {/* ── Success Toast — bottom on mobile, top-right on desktop ── */}
      <Fade in={saved}>
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{
            position: "fixed",
            top: { xs: "auto", sm: 68 },
            bottom: { xs: 16, sm: "auto" },
            right: 16,
            zIndex: 100,
            boxShadow: 4,
            borderRadius: 2,
            width: "auto",
            maxWidth: "calc(100vw - 32px)",
          }}
        >
          Quiz updated successfully
        </Alert>
      </Fade>

      {/* ── Page Content ── */}
      <Box maxWidth={820} mx="auto" px={{ xs: 1.5, sm: 3 }} py={{ xs: 2.5, sm: 4 }}>

        {/* ── Quiz Details Card ── */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600} display="block" mb={1}>
            Quiz Details
          </Typography>

          {isEditing ? (
            <TextField
              fullWidth label="Title"
              value={formData.title} onChange={handleChange("title")}
              size="small" sx={{ mb: 3 }}
            />
          ) : (
            <Typography variant="h5" fontWeight={700} mb={3}>
              {formData.title}
            </Typography>
          )}

          {isEditing ? (
            // 3-col on md+, 2-col on sm, 1-col on xs
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }} gap={2}>
              <TextField label="Time Limit (min)" type="number" size="small"
                value={formData.estimated_time_req} onChange={handleChange("estimated_time_req")} />
              <TextField label="Passing Score (%)" type="number" size="small"
                value={formData.passing_score} onChange={handleChange("passing_score")} />
              <TextField label="Attempts Allowed" type="number" size="small"
                value={formData.attempts_allowed} onChange={handleChange("attempts_allowed")} />
            </Box>
          ) : (
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <StatCard label="Time Limit" value={formData.estimated_time_req} unit="min" />
              <StatCard label="Passing Score" value={formData.passing_score} unit="%" />
              <StatCard label="Attempts" value={formData.attempts_allowed} />
              <StatCard label="Questions" value={questions.length} />
            </Stack>
          )}
        </Paper>

        {/* ── Modules Card ── */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600} display="block" mb={1}>
            Assigned Modules
          </Typography>

          {isEditing ? (
            <Autocomplete
              multiple
              options={allModules}
              getOptionLabel={(o) => o.name || ""}
              value={selectedModules}
              onChange={(_, val) => setSelectedModules(val)}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Modules"
                  size="small"
                  placeholder="Search and assign modules…"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : selectedModules.length === 0 ? (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No modules assigned
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {selectedModules.map((m) => (
                <Chip
                  key={m.id}
                  label={m.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Stack>
          )}
        </Paper>

        {/* ── Questions Section ── */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={2}>
<Box display="flex" alignItems="center" gap={1}>
  <Typography variant="h5" fontWeight={800}>
    {questions.length}
  </Typography>

  <Typography variant="body2" color="text.secondary">
    question{questions.length !== 1 ? "s" : ""}
  </Typography>
</Box>
          {isEditing && (
            <Button
              variant="contained" startIcon={<AddIcon />} size="small"
              onClick={addQuestion} sx={{ flexShrink: 0 }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Add Question</Box>
              <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Add</Box>
            </Button>
          )}
        </Box>

        {questions.length === 0 && (
          <Paper
            variant="outlined"
            sx={{ borderRadius: 3, p: { xs: 4, sm: 6 }, textAlign: "center", borderStyle: "dashed" }}
          >
            <Typography fontSize="2rem" mb={1}>📝</Typography>
            <Typography fontWeight={600} mb={0.5}>No questions yet</Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing
                ? 'Click "Add Question" to get started.'
                : "This quiz has no questions."}
            </Typography>
          </Paper>
        )}

        {questions.map((q, i) => (
          <QuestionCard
            key={i} q={q} i={i}
            isEditing={isEditing}
            updateQuestion={updateQuestion}
            updateOption={updateOption}
            removeQuestion={removeQuestion}
          />
        ))}

        {/* ── Mobile-only Cancel button at bottom when editing ── */}
        {isEditing && (
          <Box display={{ xs: "block", sm: "none" }} mt={2}>
            <Button
              variant="outlined" fullWidth
              onClick={handleCancel} disabled={saving}
            >
              Cancel Edit
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QuizDetailPage;